import { useState } from 'react';

export default function CTASection() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('company_alt') as HTMLInputElement)?.value || '';
    if (honeypot.trim() !== '') {
      setSubmitted(true);
      setSubmitting(false);
      return;
    }

    const formData = new FormData(form);
    const name = (formData.get('name') as string || '').trim();
    const phone = (formData.get('phone') as string || '').trim();
    const company = (formData.get('company') as string || '').trim();
    const employeesCount = (formData.get('employees_count') as string || '').trim();
    const channels = formData.getAll('channels').join(', ');
    const comment = (formData.get('comment') as string || '').trim();

    let cleanPhone = phone.replace(/[^\d+]/g, '');
    if (!cleanPhone.startsWith('+')) cleanPhone = '+' + cleanPhone;

    const baseUrl = 'https://businesstrainer.bitrix24.by/rest/1/yxbm0u72myjrxywc/crm.lead.add.json';
    const comments = [
      comment,
      employeesCount ? `Сотрудников: ${employeesCount}` : '',
      channels ? `Каналы: ${channels}` : '',
    ].filter(Boolean).join('. ');
    const params = new URLSearchParams({
      'fields[NAME]': name,
      'fields[TITLE]': `Лид с businesstraining ${name}`,
      'fields[COMPANY_TITLE]': company,
      'fields[PHONE][0][VALUE]': cleanPhone,
      'fields[COMMENTS]': comments,
      'params[REGISTER_SONET_EVENT]': 'Y',
      'ASSIGNED_BY_ID': '1',
    });

    fetch(`${baseUrl}?${params.toString()}`, { method: 'GET' })
      .then(async (res) => {
        if (res.ok) {
          const result = await res.json();
          if (result.result) {
            setSubmitted(true);
            form.reset();
          } else {
            throw new Error('Ошибка отправки');
          }
        } else {
          throw new Error('Ошибка отправки');
        }
      })
      .catch(() => setError('Произошла ошибка. Попробуйте ещё раз.'))
      .finally(() => setSubmitting(false));
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-gradient-to-br from-teal-600 to-teal-700 relative overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20elegant%20geometric%20pattern%20with%20soft%20overlapping%20circles%20and%20flowing%20lines%20in%20teal%20and%20white%20tones%20modern%20corporate%20background%20design%20minimal%20artistic%20texture%20sophisticated%20clean%20aesthetic&width=1920&height=800&seq=text-sales-cta-bg&orientation=landscape"
          alt=""
          className="w-full h-full object-cover opacity-10"
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Хотите, чтобы менеджеры продавали даже в переписке?
          </h2>
          <p className="text-lg text-white/80 leading-relaxed">
            Разберём реальные диалоги вашей команды, покажем, где теряются клиенты, и научим сотрудников доводить переписку до сделки.
          </p>
        </div>

        <div className="max-w-xl mx-auto">
          {submitted ? (
            <div className="bg-white/15 backdrop-blur-md rounded-2xl p-8 text-center border border-white/20">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Спасибо за заявку!</h3>
              <p className="text-white/80">Я свяжусь с вами в ближайшее время, чтобы обсудить программу под ваши задачи.</p>
            </div>
          ) : (
            <form data-readdy-form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 md:p-8 shadow-xl">
              {error && (
                <div className="bg-red-50 text-red-600 text-sm rounded-lg p-3 mb-4">
                  {error}
                </div>
              )}
              <div className="space-y-4">
                <div>
                  <label htmlFor="ts_name" className="block text-sm font-semibold text-gray-700 mb-1.5">Имя</label>
                  <input
                    type="text" id="ts_name" name="name" required
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none transition-colors"
                    placeholder="Ваше имя"
                  />
                </div>
                <div>
                  <label htmlFor="ts_phone" className="block text-sm font-semibold text-gray-700 mb-1.5">Телефон</label>
                  <input
                    type="tel" id="ts_phone" name="phone" required
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none transition-colors"
                    placeholder="+375 (__) ___ __ __"
                  />
                </div>
                <div>
                  <label htmlFor="ts_company" className="block text-sm font-semibold text-gray-700 mb-1.5">Компания</label>
                  <input
                    type="text" id="ts_company" name="company" required
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none transition-colors"
                    placeholder="Название компании"
                  />
                </div>
                <div>
                  <label htmlFor="ts_employees" className="block text-sm font-semibold text-gray-700 mb-1.5">Количество сотрудников</label>
                  <select
                    id="ts_employees" name="employees_count"
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none transition-colors bg-white"
                  >
                    <option value="">Выберите...</option>
                    <option value="1-10">1–10</option>
                    <option value="11-30">11–30</option>
                    <option value="31-100">31–100</option>
                    <option value="101-300">101–300</option>
                    <option value="300+">Более 300</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Какие каналы используются</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { id: 'ts_tg', name: 'channels', value: 'Telegram', label: 'Telegram' },
                      { id: 'ts_wa', name: 'channels', value: 'WhatsApp', label: 'WhatsApp' },
                      { id: 'ts_crm', name: 'channels', value: 'CRM', label: 'CRM (Bitrix24/AmoCRM)' },
                      { id: 'ts_email', name: 'channels', value: 'Email', label: 'E-mail' },
                    ].map((ch) => (
                      <label key={ch.id} htmlFor={ch.id} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2.5 cursor-pointer hover:bg-teal-50 transition-colors border border-gray-100">
                        <input type="checkbox" id={ch.id} name={ch.name} value={ch.value} className="w-4 h-4 text-teal-600 rounded border-gray-300 focus:ring-teal-500" />
                        <span className="text-sm text-gray-700">{ch.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label htmlFor="ts_comment" className="block text-sm font-semibold text-gray-700 mb-1.5">Комментарий</label>
                  <textarea
                    id="ts_comment" name="comment" rows={3} maxLength={500}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none transition-colors resize-none"
                    placeholder="Опишите ваши задачи или вопросы"
                  ></textarea>
                  <p className="text-xs text-gray-400 mt-1">До 500 символов</p>
                </div>

                <div className="form-honeypot" aria-hidden="true">
                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                    style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-teal-600 text-white px-6 py-4 rounded-lg text-base font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Отправка...' : 'Получить программу обучения'}
                </button>
              </div>
              <p className="text-xs text-gray-400 text-center mt-4">
                Нажимая кнопку, вы соглашаетесь с обработкой персональных данных.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}